import { addPlanToGroups, deletePlanFromGroups } from '@db/Groups';
import { addPhotosToExistingPlan } from '@db/Photos';
import { addPeopleToPlan } from '@db/Plans/Add';
import { formatPlanInput } from '@db/Plans/Common';
import { getPlan } from '@db/Plans/Get';
import { getPlanContactInviteIds, getPlanUserInviteIds } from '@db/Plans/Request';
import { logError } from '@helpers/Loggers';
import db, { updateDocWithTimestamps } from '@helpers/firebase/Firestore';
import { firestore } from '@helpers/firebase/Initialize';
import { Person, Plan, PlanFormData } from '@types';
import { Place } from '@types';

export type UpdatePlanData = Pick<
  PlanFormData,
  'description' | 'endDate' | 'joinsRequested' | 'location' | 'notes' | 'photos' | 'startDate'
>;

export async function updatePlan(
  planId: string,
  inputPlan: UpdatePlanData,
  groupIds: string[],
  people: Person[]
): Promise<string> {
  const { location, photos, ...plan } = formatPlanInput(inputPlan);
  await updatePlanDoc(planId, {
    ...plan,
    location,
  });
  const planDoc = await getPlan(planId);
  try {
    await Promise.all([
      addPhotosToExistingPlan(planId, planDoc, photos),
      updatePlanPeople(planId, people, location),
      updatePlanGroups(planId, groupIds, planDoc),
    ]);
  } catch (error) {
    // TODO: potentially handle this error differently
    logError(error);
  }
  return planId;
}

export async function updatePlanGroups(planId: string, groupIds: string[], plan: Plan) {
  const beforeGroupIds = new Set(plan.groupIds);
  const afterGroupIds = new Set(groupIds);
  const addGroupIds = [...afterGroupIds].filter((groupId) => !beforeGroupIds.has(groupId));
  const removeGroupIds = [...beforeGroupIds].filter((groupId) => !afterGroupIds.has(groupId));
  await addPlanToGroups(addGroupIds, planId);
  await deletePlanFromGroups(removeGroupIds, planId);
}

// TODO: delete people from invitation if they are removed
export async function updatePlanPeople(planId: string, people: Person[], location: Place) {
  const existingContactIds = new Set(await getPlanContactInviteIds(planId));
  const existingUserIds = new Set(await getPlanUserInviteIds(planId));
  const peopleToAdd = people.filter(
    (person) => !existingContactIds.has(person.personId) && !existingUserIds.has(person.personId)
  );
  await addPeopleToPlan(planId, peopleToAdd, location);
}

export async function updatePlanDoc(planId: string, plan: Partial<PlanFormData>) {
  await updateDocWithTimestamps(db.doc(firestore, 'plans', planId), plan);
}
